import { useCallback, useEffect, useState } from "react";
import { ArrowDownLeft, ArrowUpRight, Search } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

export default function Transactions() {
  const [meta, setMeta] = useState(null);
  const [rows, setRows] = useState([]);
  const [type, setType] = useState("");
  const [days, setDays] = useState(90);
  const [q, setQ] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/store/transactions", {
        params: { txn_type: type, days, limit: 500, date_from: from, date_to: to },
      });
      setRows(data);
    } catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  }, [type, days, from, to]);

  useEffect(() => { api.get("/store/meta").then(({ data }) => setMeta(data))
    .catch((e) => console.warn("store meta unavailable:", e)); }, []);
  useEffect(() => { load(); }, [load]);

  const inTypes = meta?.txn_types?.in || [];
  const filtered = rows.filter((r) =>
    !q || `${r.item_name} ${r.item_code} ${r.lot_number} ${r.performed_by} ${r.grn_no}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <div data-testid="transactions-page">
      <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight text-[#111827]">Stock Transactions</h1>
      <p className="text-sm text-[#6B7280] mt-1 max-w-2xl">
        The single source of truth for stock. Receipts, issues, consumption, wastage and adjustments recorded
        here — or generated when an indent is received — instantly recalculate every balance and status.
      </p>

      <div className="mt-5 flex flex-wrap gap-2">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#6B7280]" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} data-testid="txn-search"
            placeholder="Search item, lot, GRN or person…" className="pl-9 bg-white" />
        </div>
        <select value={type} onChange={(e) => setType(e.target.value)} data-testid="txn-type-filter"
          className="h-10 rounded-md border border-[#E5E7EB] bg-white px-3 text-sm">
          <option value="">All movements</option>
          {[...(meta?.txn_types?.in || []), ...(meta?.txn_types?.out || [])].map((t) => (
            <option key={t} value={t}>{t}</option>
          ))}
        </select>
        <select value={days} onChange={(e) => setDays(Number(e.target.value))} data-testid="txn-days-filter"
          className="h-10 rounded-md border border-[#E5E7EB] bg-white px-3 text-sm">
          {[30, 90, 180, 365].map((d) => <option key={d} value={d}>{`Last ${d} days`}</option>)}
        </select>
        <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} title="From date"
          className="h-10 w-40 bg-white" data-testid="txn-date-from" />
        <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} title="To date"
          className="h-10 w-40 bg-white" data-testid="txn-date-to" />
      </div>

      <div className="mt-4 rounded-md border border-[#E5E7EB] bg-white overflow-x-auto">
        <table className="w-full text-sm" data-testid="txn-table">
          <thead className="bg-[#F9FAFB]">
            <tr className="text-left text-[10px] uppercase tracking-[0.1em] text-[#6B7280]">
              <th className="px-4 py-2.5">Date</th>
              <th className="px-4 py-2.5">Item</th>
              <th className="px-4 py-2.5">Movement</th>
              <th className="px-4 py-2.5 text-right">Qty</th>
              <th className="px-4 py-2.5">Lot / Expiry</th>
              <th className="px-4 py-2.5">Reference</th>
              <th className="px-4 py-2.5">By</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#E5E7EB]">
            {loading && <tr><td colSpan={7} className="px-4 py-8 text-center text-[#6B7280]">Loading…</td></tr>}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-[#6B7280]" data-testid="txn-empty">
                No movements in this period.</td></tr>
            )}
            {filtered.map((t) => {
              const inward = inTypes.includes(t.txn_type);
              return (
                <tr key={t.txn_id} className="hover:bg-[#F9FAFB]" data-testid={`txn-row-${t.txn_id}`}>
                  <td className="px-4 py-2.5 text-xs">{t.txn_date}</td>
                  <td className="px-4 py-2.5">
                    <p className="text-[#111827]">{t.item_name}</p>
                    <p className="text-xs text-[#6B7280]">{t.item_code}</p>
                  </td>
                  <td className="px-4 py-2.5">
                    <span className={`inline-flex items-center gap-1 text-xs ${inward ? "text-[#2E7D32]" : "text-[#B45309]"}`}>
                      {inward ? <ArrowDownLeft className="h-3.5 w-3.5" /> : <ArrowUpRight className="h-3.5 w-3.5" />}
                      {t.txn_type}
                    </span>
                  </td>
                  <td className={`px-4 py-2.5 text-right font-semibold ${inward ? "text-[#2E7D32]" : "text-[#B45309]"}`}>
                    {inward ? "+" : "−"}{t.quantity} <span className="text-xs font-normal text-[#6B7280]">{t.stock_unit}</span>
                  </td>
                  <td className="px-4 py-2.5 text-xs">{t.lot_number || "—"}{t.expiry_date ? ` · ${t.expiry_date}` : ""}</td>
                  <td className="px-4 py-2.5 text-xs">{t.grn_no || t.issued_to || t.reason || "—"}</td>
                  <td className="px-4 py-2.5 text-xs text-[#6B7280]">{t.performed_by}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
